import { useDocumentsStorageApi } from 'infrastructure/api';
import {
  getFilePreview,
  getFileType,
  getFileTypeFromName,
  appendArtifacts,
  removeArtifacts,
} from 'domain/shared/artifact';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DocumentStorageItemType, useDocumentStorageContext } from 'framework/documentStorage';

export const normalizeStoragePath = (path: string) => (path?.startsWith?.('/') ? path.slice(1) : path);
export const normalizeStoragePathWithSlash = (path: string) => (path?.startsWith?.('/') ? path : `/${path}`);

const mapToFileData = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', file.name);
  return formData;
};

const useDocumentStorageService = () => {
  const { getDocumentToken } = useDocumentStorageContext({ ignoreContext: true });
  const { uploadFile } = useDocumentsStorageApi('/document-storage');
  const dispatch = useDispatch();
  const artifacts = useSelector((state: any) => state.artifacts, shallowEqual);
  const setArtifactsToStore = (artifactsData: any) => dispatch(appendArtifacts(artifactsData));

  const removeArtifactsFromStore = (artifactPaths: string[]) => dispatch(removeArtifacts(artifactPaths));

  const downloadDocument = async (documentPath: string): Promise<Blob> => {
    const link = await getDocumentToken(normalizeStoragePath(documentPath));
    const response = await fetch(link);

    if (!response.ok) {
      throw new Error(`Failed to load template`);
    }

    return await response.blob();
  };

  const uploadArtifactRaw = async ({ file, signal, onProgress }: any) => {
    const { response } = await uploadFile({
      url: '/create/temp',
      data: mapToFileData(file),
      eventTarget: signal,
      onProgress,
      baseUrl: '',
    });

    return JSON.parse(response);
  };

  const uploadArtifact = async ({ file, signal, onProgress }: any) => {
    const data = await uploadArtifactRaw({ file, signal, onProgress });
    const { id } = data; // { id, path } AND id === path

    const filePreview = await getFilePreview(file);
    const type = getFileType(file);

    setArtifactsToStore({
      [id]: {
        filePreview,
        id,
        type,
        name: file.name,
        title: file.name,
      },
    });

    return id || null;
  };

  const mapDocStorageDataToArtifactStore = (documentData: DocumentStorageItemType) => {
    const { fileName = 'Document' } = documentData?.metadata || {};
    const type = getFileTypeFromName(fileName);

    return {
      ...documentData,
      type,
      name: fileName,
      title: fileName,
    };
  };

  return {
    artifacts,
    downloadDocument,
    uploadArtifact,
    uploadArtifactRaw,
    setArtifactsToStore,
    removeArtifactsFromStore,
    mapDocStorageDataToArtifactStore,
  };
};

export default useDocumentStorageService;
